import { ArrowLeft, ArrowRight, CheckCircle2, ChevronRight } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import ScandicDevLogo from "@/components/ScandicDevLogo";
import Seo, { JsonLd, buildBreadcrumbSchema, buildServiceSchema } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { marketingPageMap } from "@/content/marketingPages";
import { getCanonicalUrl } from "@/lib/site";

function RelatedLinkCard({ page }) {
  return (
    <Link
      to={page.path}
      className="group rounded-2xl border border-zinc-800 bg-zinc-900/50 p-5 transition-colors hover:border-violet-500/40"
    >
      <p className="text-sm text-violet-300 mb-2">
        {page.kind === "service" ? "Leistung" : "Branche"}
      </p>
      <h3 className="text-xl font-semibold text-white font-['Space_Grotesk'] mb-2">
        {page.h1}
      </h3>
      <p className="text-zinc-400 text-sm">{page.description}</p>
      <span className="inline-flex items-center gap-2 text-violet-300 mt-4 text-sm">
        Mehr erfahren
        <ChevronRight className="w-4 h-4" />
      </span>
    </Link>
  );
}

export default function MarketingDetailPage() {
  const location = useLocation();
  const page = marketingPageMap[location.pathname];

  if (!page) {
    return null;
  }

  const breadcrumbItems =
    page.kind === "industry"
      ? [
          { name: "Startseite", path: "/" },
          { name: "Branchen", path: "/" },
          { name: page.h1, path: page.path },
        ]
      : [
          { name: "Startseite", path: "/" },
          { name: page.h1, path: page.path },
        ];

  const relatedPages = page.relatedPaths
    .map((path) => marketingPageMap[path])
    .filter(Boolean);

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-zinc-300">
      <Seo
        title={page.title}
        description={page.description}
        path={page.path}
        language="de"
        type="website"
      />
      <JsonLd data={buildBreadcrumbSchema(breadcrumbItems)} />
      {page.kind === "service" ? (
        <JsonLd
          data={buildServiceSchema({
            name: page.h1,
            description: page.description,
            path: page.path,
            audience: "Unternehmen im deutschen Mittelstand",
          })}
        />
      ) : null}

      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#0A0A0B]/85 border-b border-zinc-800">
        <div className="container-main">
          <div className="relative flex h-20 md:h-24 items-center justify-center md:justify-between gap-4">
            <Link to="/" className="flex items-center gap-3 text-zinc-300 hover:text-white transition-colors">
              <ScandicDevLogo className="h-14 w-auto object-contain md:h-16" />
            </Link>

            <nav className="hidden md:flex items-center gap-6 text-sm text-zinc-400">
              <a href="/#leistungen" className="hover:text-white transition-colors">
                Leistungen
              </a>
              <a href="/#branchen-links" className="hover:text-white transition-colors">
                Branchen
              </a>
              <a href="/#kontakt" className="hover:text-white transition-colors">
                Kontakt
              </a>
            </nav>

            <Button asChild className="hidden md:inline-flex bg-violet-600 hover:bg-violet-700 text-white rounded-full px-6">
              <a href="/#kontakt">Kostenlose Prozess-Analyse</a>
            </Button>
          </div>
        </div>
      </header>

      <main>
        <section className="hero-bg pt-24 pb-16 md:pt-32 md:pb-24">
          <div className="container-main relative z-10">
            <div className="max-w-4xl">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors mb-6"
              >
                <ArrowLeft className="w-4 h-4" />
                Zur Startseite
              </Link>

              <p className="text-sm uppercase tracking-[0.2em] text-violet-300 mb-4">
                {page.kind === "service" ? "Leistung" : "Branche"}
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 font-['Space_Grotesk']">
                {page.h1}
              </h1>
              <p className="text-lg md:text-xl text-zinc-400 max-w-3xl mb-10">
                {page.intro}
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild size="lg" className="bg-violet-600 hover:bg-violet-700 text-white rounded-full px-8">
                  <a href="/#kontakt">
                    Prozesspotenzial besprechen
                    <ArrowRight className="w-5 h-5" />
                  </a>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white rounded-full px-8"
                >
                  <a href={getCanonicalUrl(page.path)}>Kanonische URL öffnen</a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 md:py-28">
          <div className="container-main">
            <div className="grid gap-10 lg:grid-cols-[1.1fr_0.9fr]">
              <div>
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6 font-['Space_Grotesk']">
                  Wo diese Seite konkret Mehrwert schafft
                </h2>
                <ul className="space-y-4">
                  {page.benefits.map((benefit) => (
                    <li key={benefit} className="flex gap-3 rounded-2xl border border-zinc-800 bg-zinc-900/40 p-5">
                      <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                      <span className="text-zinc-300">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-3xl border border-violet-500/20 bg-violet-500/10 p-8">
                <h2 className="text-2xl font-bold text-white mb-4 font-['Space_Grotesk']">
                  DSGVO-konforme Umsetzung
                </h2>
                <p className="text-zinc-300 mb-4">
                  Unsere Lösungen werden entlang Ihrer tatsächlichen Datenflüsse geplant. Dazu gehören klare Regeln für
                  Zugriffe, Freigaben, Eskalationen und menschliche Kontrolle.
                </p>
                <p className="text-zinc-400">
                  Gerade im deutschen Mittelstand ist nicht die Demo entscheidend, sondern der belastbare Betrieb in
                  bestehenden Abläufen.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 md:py-28 bg-zinc-900/30">
          <div className="container-main">
            <div className="text-center max-w-3xl mx-auto mb-14">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 font-['Space_Grotesk']">
                Typische Einsatzfelder
              </h2>
              <p className="text-zinc-400">
                Die folgenden Beispiele zeigen, wie wir {page.kind === "service" ? "Leistungen" : "Prozesse"} konkret
                in operative Abläufe übersetzen.
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-3">
              {page.useCases.map((useCase) => (
                <div key={useCase.title} className="rounded-2xl border border-zinc-800 bg-zinc-900/50 p-6">
                  <h3 className="text-xl font-semibold text-white mb-3 font-['Space_Grotesk']">{useCase.title}</h3>
                  <p className="text-zinc-400">{useCase.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 md:py-28">
          <div className="container-main">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-8 text-center font-['Space_Grotesk']">
                So gehen wir in die Umsetzung
              </h2>
              <div className="space-y-4">
                {page.implementation.map((item, index) => (
                  <div
                    key={item}
                    className="flex gap-4 rounded-2xl border border-zinc-800 bg-zinc-900/40 px-6 py-5"
                  >
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-violet-500/10 text-violet-300 font-semibold">
                      {index + 1}
                    </div>
                    <p className="text-zinc-300 pt-1">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24">
          <div className="container-main">
            <div className="cta-section p-8 md:p-12 text-center relative z-10">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 text-white font-['Space_Grotesk']">
                {page.ctaTitle}
              </h2>
              <p className="text-zinc-400 mb-8 max-w-2xl mx-auto">{page.ctaText}</p>
              <Button asChild size="lg" className="bg-white text-violet-900 hover:bg-zinc-100 rounded-full px-8">
                <a href="/#kontakt">
                  Kostenlose Prozess-Analyse anfragen
                  <ArrowRight className="w-5 h-5" />
                </a>
              </Button>
            </div>
          </div>
        </section>

        <section className="py-20 border-t border-zinc-800">
          <div className="container-main">
            <div className="flex items-end justify-between gap-6 mb-8 flex-wrap">
              <div>
                <h2 className="text-3xl font-bold text-white font-['Space_Grotesk'] mb-2">
                  Weiterführende Seiten
                </h2>
                <p className="text-zinc-400">
                  Verwandte Leistungen und Branchen aus dem gleichen Lösungskontext.
                </p>
              </div>
              <a href="/#leistungen" className="text-violet-300 hover:text-violet-200 transition-colors">
                Zur Leistungsübersicht
              </a>
            </div>

            <div className="grid gap-6 md:grid-cols-3">
              {relatedPages.map((relatedPage) => (
                <RelatedLinkCard key={relatedPage.path} page={relatedPage} />
              ))}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
